import { CheckIcon } from "@radix-ui/react-icons";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { PublicFormsService, PublicPaymentSession } from "client/openapi";
import { MutableRefObject, useEffect, useMemo, useState } from "react";

export type PaymentResult = { setupIntentId: string | null; error?: string };

/** Lets the page save the payment method right before it submits the answers. */
export type PaymentHandle = { confirm: () => Promise<PaymentResult> };

const PUBLISHABLE_KEY = process.env
  .REACT_APP_STRIPE_PUBLISHABLE_KEY_TADPOLE_BILLING as string;

function Header({ required }: { required: boolean }) {
  return (
    <>
      <h2 className="text-lg font-semibold text-gray-900">Payment</h2>
      <p className="mt-1 mb-4 text-sm text-gray-600">
        {required
          ? "Save a payment method for future sessions. You are not charged now."
          : "Save a payment method for future sessions, if you like. You are not charged now."}
      </p>
    </>
  );
}

function ErrorText({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p className="mt-2 text-sm text-red-600" data-form-error>
      {error}
    </p>
  );
}

function Fields({
  handle,
  disabled,
  onUseAnother,
}: {
  handle: MutableRefObject<PaymentHandle | null>;
  disabled: boolean;
  onUseAnother: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [input, setInput] = useState({ empty: true, complete: false });
  const [savedId, setSavedId] = useState<string | null>(null);

  // Refreshed every render, so the page always confirms what is on screen.
  useEffect(() => {
    handle.current = {
      confirm: async () => {
        if (savedId) return { setupIntentId: savedId };
        // Nothing entered or chosen: the server decides if that is allowed.
        // Checked first, so an optional step never waits on Stripe.
        if (input.empty && !input.complete) return { setupIntentId: null };
        if (!stripe || !elements) {
          return {
            setupIntentId: null,
            error: "The payment form is still loading. Try again in a moment.",
          };
        }
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          // Redirect methods are turned off for this SetupIntent, so this
          // never leaves the page; Stripe still asks for a return address.
          redirect: "if_required",
          confirmParams: { return_url: window.location.href },
        });
        if (error) {
          // Stripe saved it on an earlier try whose reply was lost. The
          // server checks this id with Stripe like any other.
          if (error.setup_intent?.status === "succeeded") {
            setSavedId(error.setup_intent.id);
            return { setupIntentId: error.setup_intent.id };
          }
          return {
            setupIntentId: null,
            error:
              error.type === "validation_error"
                ? "Check your payment details."
                : error.message || "This payment method could not be saved.",
          };
        }
        if (setupIntent?.status === "succeeded") {
          setSavedId(setupIntent.id);
          return { setupIntentId: setupIntent.id };
        }
        return {
          setupIntentId: null,
          error: "This payment method could not be saved. Try another one.",
        };
      },
    };
  });

  useEffect(
    () => () => {
      handle.current = null;
    },
    [handle]
  );

  if (savedId) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-3">
        <span className="flex items-center gap-2 text-sm text-green-900">
          <CheckIcon /> Your payment method is saved.
        </span>
        <button
          type="button"
          className="text-sm font-medium text-gray-700 underline"
          onClick={onUseAnother}
          disabled={disabled}
        >
          Use a different one
        </button>
      </div>
    );
  }

  return (
    <PaymentElement
      options={{ readOnly: disabled }}
      onChange={(e) => setInput({ empty: e.empty, complete: e.complete })}
    />
  );
}

/**
 * Stripe's Payment Element for a form link: the person's saved cards and
 * banks (they can remove them) and a place to add one. Nothing is charged.
 */
export default function PaymentStep({
  token,
  required,
  handle,
  error,
  disabled = false,
}: {
  token: string;
  required: boolean;
  handle: MutableRefObject<PaymentHandle | null>;
  error?: string;
  disabled?: boolean;
}) {
  const [session, setSession] = useState<PublicPaymentSession>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setSession(undefined);
    setFailed(false);
    PublicFormsService.startPublicFormPayment({ requestBody: { token } })
      .then(setSession)
      .catch(() => setFailed(true));
  }, [token, attempt]);

  const account = session?.stripe_account_id || null;
  const loaded = !!session;
  const stripePromise = useMemo(
    () =>
      loaded
        ? loadStripe(
            PUBLISHABLE_KEY,
            account ? { stripeAccount: account } : undefined
          ).catch(() => {
            // Stripe.js did not load: show Try again, and let the form go
            // without a payment method (the server decides if it may).
            setFailed(true);
            return null;
          })
        : null,
    [loaded, account]
  );

  let body;
  if (failed) {
    body = (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
        Payment methods cannot be added right now.{" "}
        <button
          type="button"
          className="font-medium underline"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try again
        </button>
      </div>
    );
  } else if (!session || !stripePromise) {
    body = (
      <div className="animate-pulse space-y-3">
        <div className="h-11 rounded bg-gray-100" />
        <div className="h-11 rounded bg-gray-100" />
      </div>
    );
  } else {
    // customerSessionClientSecret shows the saved methods; the type for it
    // arrived after the stripe-js version this app uses.
    const options = {
      clientSecret: session.setup_intent_client_secret,
      customerSessionClientSecret: session.customer_session_client_secret,
      appearance: { theme: "stripe" },
    } as StripeElementsOptions;
    body = (
      <Elements
        key={session.setup_intent_client_secret}
        stripe={stripePromise}
        options={options}
      >
        <Fields
          handle={handle}
          disabled={disabled}
          onUseAnother={() => setAttempt((n) => n + 1)}
        />
      </Elements>
    );
  }

  return (
    <section className="mt-8">
      <Header required={required} />
      {body}
      <ErrorText error={error} />
    </section>
  );
}

/** What the builder's preview shows in place of Stripe's form. */
export function PaymentPreview({ required }: { required: boolean }) {
  return (
    <section className="mt-8">
      <Header required={required} />
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
        Their saved cards and a place to add one appear here.
      </div>
    </section>
  );
}
